import {
  Component,
  DestroyRef,
  signal,
  output,
  input,
  computed,
  TemplateRef,
  inject,
} from '@angular/core';
import { NttAlert } from './alert';
import { ToastAction, ToastType } from './toast.config';

export interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
  duration: number;
  dismissible: boolean;
  pauseOnHover: boolean;
  timer: number | null;
  paused: boolean;
  startTime: number;
  remaining: number;
  actionsTemplate?: TemplateRef<unknown>;
  action?: ToastAction;
}

@Component({
  selector: 'ntt-toast-container',
  imports: [NttAlert],
  template: `
    @for (toast of toasts(); track toast.id) {
      <ntt-alert
        [type]="toast.type"
        [message]="toast.message"
        [dismissible]="toast.dismissible"
        [actionsTemplate]="toast.actionsTemplate"
        [action]="toast.action"
        (dismiss)="remove(toast.id)"
        (enter)="pause(toast.id)"
        (leave)="resume(toast.id)"
      />
    }
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-sm, 6px);
      max-width: 343px;
      pointer-events: none;
    }

    :host(.ntt-toast-container--top) {
      flex-direction: column-reverse;
    }

    ntt-alert {
      display: block;
      pointer-events: auto;
    }
  `,
  host: {
    '[class.ntt-toast-container--top]': 'isTop()',
  },
})
export class NttToastContainer {
  readonly position = input<string>('top-center');
  readonly isTop = computed(() => this.position().startsWith('top'));

  readonly toasts = signal<ToastItem[]>([]);
  readonly empty = output<void>();

  private _alive = true;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this._alive = false;
      this.clear();
    });
  }

  add(toast: ToastItem): void {
    if (!this._alive) return;

    this.toasts.update((t) => [...t, toast]);

    if (toast.duration > 0) {
      toast.timer = window.setTimeout(
        () => this.remove(toast.id),
        toast.duration,
      );
    }
  }

  remove(id: string): void {
    const toast = this.toasts().find((t) => t?.id === id);
    if (toast?.timer) {
      clearTimeout(toast.timer);
    }

    this.toasts.update((t) => t.filter((x) => x?.id !== id));

    if (this._alive && this.toasts().length === 0) {
      this.empty.emit();
    }
  }

  pause(id: string): void {
    if (!this._alive) return;

    const current = this.toasts().find((t) => t?.id === id);
    if (!current?.pauseOnHover || current.paused) return;

    if (current.timer) {
      clearTimeout(current.timer);
    }

    const startedAt = current.startTime ?? Date.now();
    const remaining =
      (current.remaining ?? current.duration ?? 0) - (Date.now() - startedAt);

    this.toasts.update((toasts) =>
      toasts.map((toast) =>
        toast?.id === id
          ? { ...toast, paused: true, timer: null, remaining }
          : toast,
      ),
    );
  }

  resume(id: string): void {
    if (!this._alive) return;

    const current = this.toasts().find((t) => t?.id === id);
    if (!current?.paused) return;

    const remaining = current.remaining ?? 0;
    const newTimer =
      remaining > 0
        ? window.setTimeout(() => this.remove(id), remaining)
        : null;

    this.toasts.update((toasts) =>
      toasts.map((toast) =>
        toast?.id === id
          ? { ...toast, paused: false, startTime: Date.now(), timer: newTimer }
          : toast,
      ),
    );
  }

  clear(): void {
    this.toasts().forEach((toast) => {
      if (toast?.timer) clearTimeout(toast.timer);
    });
    this.toasts.set([]);
  }
}
